import React from 'react'
import { observer } from 'mobx-react-lite'
import { addMonths } from 'date-fns/addMonths'
import { alpha } from '@mui/material/styles'

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import { LocalizationProvider } from '@mui/x-date-pickers-pro'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'

import { CurrencyInput, Dialog, useAccountStore, useGlobalCopyStore } from '../../../../common'
import { type AccountField } from '../../../utils/AccountFields'
import { useAccountUiStore } from '../../../hooks/useUiStore'
import { FieldType } from '../../../constants/Accounts'
import { buildDynamicCopy } from '../../../../core/utils/Localization'

interface AccountDetailEditProps {
  field: AccountField
  isOpen: boolean
  onClose: () => void
}

const AccountDetailEdit: React.FC<AccountDetailEditProps> = ({ field, isOpen, onClose }) => {
  const { updateAccount } = useAccountStore()
  const { selectedAccount: account } = useAccountUiStore()
  const { accounts: accountsCopy } = useGlobalCopyStore()

  const [editedValue, setEditedValue] = React.useState<any>(account[field.name])
  const [errorMessage, setErrorMessage] = React.useState('')
  const [hasValidCurrencyInput, setHasValidCurrencyInput] = React.useState(true)

  const handleClose = () => {
    setEditedValue('')
    setErrorMessage('')
    onClose()
  }

  const getEditableField = () => {
    switch (field.type) {
      case FieldType.Select: {
        return (
          <FormControl fullWidth={true}>
            <InputLabel id={`${field.label}-select-label`}>{field.label}</InputLabel>
            <Select
              displayEmpty={true}
              id={`${field.label}-select`}
              label={field.label}
              labelId={`${field.label}-select-label`}
              onChange={(e) => setEditedValue(e.target.value)}
              slotProps={{
                input: {
                  sx: {
                    backgroundColor: 'background.paper',
                  },
                },
              }}
              value={editedValue}
            >
              {!editedValue && (
                <MenuItem disabled={true} value="">
                  {field.placeholder}
                </MenuItem>
              )}
              {field.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      }

      case FieldType.Currency: {
        return (
          <CurrencyInput
            amount={(editedValue as string | number) || ''}
            ariaLabel={field.placeholder}
            fullWidth={true}
            label={field.label}
            placeholder={field.placeholder}
            setAmount={setEditedValue}
            setHasValidInput={setHasValidCurrencyInput}
          />
        )
      }

      case FieldType.Date: {
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              maxDate={addMonths(new Date(), 1)}
              minDate={new Date()}
              onChange={(date: Date) => {
                if (account[field.name] && Number(account[field.name]) <= 31) {
                  // gets and sets the day of the month to editedValue
                  setEditedValue(date.getDate())
                } else {
                  // the day of the month was already passed, so we set it directly to state
                  setEditedValue(date)
                }
              }}
              sx={{
                '& .MuiPickersDay-today': {
                  color: 'primary.lighter',
                },
                '& .MuiPickersArrowSwitcher-button': {
                  color: 'primary.lighter',
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.light, 0.1),
                  },
                },
              }}
              views={['day']}
            />
          </LocalizationProvider>
        )
      }

      default: {
        return (
          <TextField
            defaultValue={account[field.name]}
            error={!!errorMessage}
            fullWidth={true}
            helperText={errorMessage}
            onChange={updateField}
            placeholder={field.placeholder}
            slotProps={{
              htmlInput: {
                'aria-label': field.placeholder,
              },
            }}
          />
        )
      }
    }
  }

  const saveField = () => {
    // prevents any unnecessary api calls
    if (account[field.name] !== editedValue) {
      updateAccount({
        ...account,
        [field.name]: editedValue,
      })
    }
    handleClose()
  }

  const updateField = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedValue(e.target.value)

    if (field.validation) {
      setErrorMessage(field.validation(e.target.value))
    }
  }

  return (
    <Dialog
      copy={{
        close_aria: accountsCopy.close_aria,
        title: buildDynamicCopy(accountsCopy.edit_field, field.label) as string,
      }}
      disablePrimaryButton={!!errorMessage || !hasValidCurrencyInput}
      isOpen={isOpen}
      onClose={handleClose}
      onPrimaryAction={saveField}
      primaryText={accountsCopy.save}
      sx={{
        '& .MuiButton-root:focus-visible, & .MuiButton-root:focus': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '2px',
        },
      }}
    >
      {getEditableField()}
    </Dialog>
  )
}

export default observer(AccountDetailEdit)
